import React, { useEffect, useState } from "react";
import { Checkbox, Group, List, Progress, SimpleGrid, Skeleton, Stack, Stepper, Text } from "@mantine/core";
import { Alert, Button, ConfirmModal, SectionCard, spacePx } from "@xplatform/design-system";
import { IconKey, IconRotateClockwise } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ROTATION_PHASES, type DatasetProgress, type Rotation } from "@models/encryptionKey";
import { useEncryptionKey } from "@hooks/encryptionKey";
import { formatShortDateTime } from "@utils/monitoringFormat";

/** The build mode is the environment (`vite build --mode sandbox|develop|production`). */
const ENVIRONMENT = import.meta.env.MODE;

const DATASETS = ["credentials", "pii", "sharepoint"] as const;

/** Seconds left until `until`, ticking every second. */
const useCountdown = (until: string | null): number | null => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [until]);
  if (!until) return null;
  return Math.max(0, Math.round((new Date(until).getTime() - now) / 1000));
};

const formatSeconds = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const DatasetBar: React.FC<{ label: string; progress: DatasetProgress }> = ({ label, progress }) => {
  const { t } = useTranslation();
  const done = progress.rotated + progress.alreadyCurrent;
  const value = progress.total > 0 ? (done / progress.total) * 100 : 100;
  return (
    <Stack gap={spacePx[1]}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>{label}</Text>
        <Text size="xs" c={progress.failed > 0 ? "red" : "dimmed"}>
          {t("admin.providers.encryption.progress", { ...progress })}
        </Text>
      </Group>
      <Progress value={value} color={progress.failed > 0 ? "red" : "teal"} aria-label={label} />
    </Stack>
  );
};

const RotationView: React.FC<{
  rotation: Rotation; canRotate: boolean; busy: boolean; onResume: () => void; onRollback: () => void;
}> = ({ rotation, canRotate, busy, onResume, onRollback }) => {
  const { t } = useTranslation();
  const remaining = useCountdown(rotation.status === "waiting" || rotation.waitingUntil ? rotation.waitingUntil : null);
  const active = rotation.status === "done" ? ROTATION_PHASES.length : ROTATION_PHASES.indexOf(rotation.phase);
  const failed = rotation.status === "failed";

  return (
    <Stack gap="md" data-testid="rotation-progress">
      <Stepper active={active} size="sm" color={failed ? "red" : undefined} allowNextStepsSelect={false}>
        {ROTATION_PHASES.map((phase) => (
          <Stepper.Step key={phase} label={t(`admin.providers.encryption.phases.${phase}`)}
                        description={t(`admin.providers.encryption.phaseHints.${phase}`)} />
        ))}
      </Stepper>
      <Text size="sm" fw={500}>{t(`admin.providers.encryption.status.${rotation.status}`)}</Text>
      {remaining !== null && remaining > 0 && (
        <Text size="sm" c="dimmed">{t("admin.providers.encryption.waiting", { time: formatSeconds(remaining) })}</Text>
      )}
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
        {DATASETS.map((d) => (
          <DatasetBar key={d} label={t(`admin.providers.encryption.datasets.${d}`)} progress={rotation.datasets[d]} />
        ))}
      </SimpleGrid>
      {rotation.errors.length > 0 && (
        <Alert color="red" title={t("admin.providers.encryption.errors")}>
          <List size="sm">
            {rotation.errors.map((e, i) => (
              <List.Item key={i}>{[e.phase, e.dataset].filter(Boolean).join(" / ")}{e.phase || e.dataset ? ": " : ""}{e.message}</List.Item>
            ))}
          </List>
        </Alert>
      )}
      {failed && canRotate && (
        <Group gap="sm">
          <Button onClick={onResume} loading={busy}>{t("admin.providers.encryption.resume")}</Button>
          {rotation.phase !== "retire" && (
            <Button variant="light" color="red" onClick={onRollback} loading={busy}>
              {t("admin.providers.encryption.rollback")}
            </Button>
          )}
        </Group>
      )}
    </Stack>
  );
};

/**
 * The platform encryption key (`/xplatform/{env}/encryption-key`): its
 * fingerprint, the last rotation, and the rotation that re-encrypts every
 * provider credential, user profile and SharePoint connection with a new key.
 * The key itself never reaches the browser.
 */
const EncryptionKeyCard: React.FC<{ canRotate: boolean }> = ({ canRotate }) => {
  const { t } = useTranslation();
  const { status, rotation, busy, actionError, start, resume, rollback } = useEncryptionKey(true);
  const [confirming, setConfirming] = useState(false);
  const [prerequisites, setPrerequisites] = useState(false);

  const running = status.data?.running ?? null;
  const shown = rotation.data && (running || rotation.data.status === "failed" || rotation.data.status === "waiting"
    || rotation.data.status === "running" || rotation.data.status === "done");

  // ConfirmModal has no disabled state: without the tick the button only
  // points at the checkbox (and core would answer 412 anyway).
  const confirm = async () => {
    if (!prerequisites) return;
    if (await start()) {
      setConfirming(false);
      setPrerequisites(false);
    }
  };

  return (
    <SectionCard
      title={
        <Group gap="xs">
          <IconKey size={18} />
          <Text fw={600}>{t("admin.providers.encryption.title")}</Text>
        </Group>
      }
      actions={canRotate && (
        <Button
          size="xs"
          variant="light"
          color="red"
          leftSection={<IconRotateClockwise size={16} />}
          disabled={!status.data || running !== null || (rotation.data?.status === "failed")}
          onClick={() => setConfirming(true)}
        >
          {t("admin.providers.encryption.rotate")}
        </Button>
      )}
    >
      <Stack gap="md">
        {status.error && (
          <Alert color={status.forbidden ? "gray" : "red"}>{status.error}</Alert>
        )}
        {!status.data && !status.error ? <Skeleton h={48} /> : status.data && (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Stack gap={0}>
              <Text size="xs" c="dimmed">{t("admin.providers.encryption.fingerprint")}</Text>
              <Text size="sm" ff="monospace" data-testid="key-fingerprint">{status.data.fingerprint}</Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="dimmed">{t("admin.providers.encryption.keys")}</Text>
              <Text size="sm">{status.data.keysInParameter}</Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="dimmed">{t("admin.providers.encryption.lastRotation")}</Text>
              <Text size="sm">
                {status.data.lastRotation
                  ? t("admin.providers.encryption.lastRotationValue", {
                      at: formatShortDateTime(status.data.lastRotation.finishedAt),
                      by: status.data.lastRotation.by ?? "-",
                    })
                  : t("admin.providers.encryption.never")}
              </Text>
            </Stack>
          </SimpleGrid>
        )}
        {rotation.error && <Alert color="red">{rotation.error}</Alert>}
        {actionError && <Alert color="red">{actionError}</Alert>}
        {shown && rotation.data && (
          <RotationView rotation={rotation.data} canRotate={canRotate} busy={busy} onResume={resume} onRollback={rollback} />
        )}
      </Stack>

      <ConfirmModal
        opened={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={confirm}
        title={t("admin.providers.encryption.confirmTitle", { environment: ENVIRONMENT })}
        confirmLabel={t("admin.providers.encryption.rotate")}
        cancelLabel={t("common.cancel")}
        loading={busy}
        danger
        portalProps={{ target: "#admin-portal-root" }}
      >
        <Stack gap="sm">
          <Text size="sm">{t("admin.providers.encryption.confirmIntro", { environment: ENVIRONMENT })}</Text>
          <List size="sm" type="ordered">
            {ROTATION_PHASES.map((p) => (
              <List.Item key={p}>
                <Text span fw={600} size="sm">{t(`admin.providers.encryption.phases.${p}`)}</Text>
                {": "}{t(`admin.providers.encryption.phaseHints.${p}`)}
              </List.Item>
            ))}
          </List>
          <Text size="sm">{t("admin.providers.encryption.duration")}</Text>
          <Alert color="yellow">{t("admin.providers.encryption.notApiKeys")}</Alert>
          <Checkbox
            checked={prerequisites}
            onChange={(e) => setPrerequisites(e.currentTarget.checked)}
            label={t("admin.providers.encryption.prerequisites")}
          />
          {!prerequisites && <Text size="xs" c="dimmed">{t("admin.providers.encryption.prerequisitesRequired")}</Text>}
          {actionError && <Alert color="red">{actionError}</Alert>}
        </Stack>
      </ConfirmModal>
    </SectionCard>
  );
};

export default EncryptionKeyCard;
